import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Activity, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { fmtWhen } from "@/components/LoginInfo";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePageMeta } from "@/lib/usePageMeta";

const VERBS = {
  page_edit: "edited a winner page", page_approve: "approved a page", page_reject: "rejected a page",
  page_trash: "moved a page to Trash", page_restore: "restored a page", page_resend_welcome: "resent a welcome email",
  page_press_release_edit: "edited a press release", page_press_release_regenerate: "regenerated a press release",
  page_press_release_approve: "approved & emailed a press release",
  permanent_delete: "permanently deleted", approved_email_add: "added an approved email",
  approved_email_bulk_add: "bulk-added approved emails", approved_email_remove: "removed an approved email",
  approved_email_bulk_remove: "bulk-removed approved emails", user_upsert: "added/updated a staff user",
  user_remove: "removed a staff user", user_resend_invite: "resent a staff invitation", site_content_edit: "edited site content / frames", purge_test_data: "removed test data",
  password_set: "set a password", password_change: "changed their password", password_remove: "removed their password", user_email_change: "changed their email to",
  view_as_winner: "previewed the page as", frames_export: "downloaded all frames for", db_export: "downloaded a full database backup of",
};
const KINDS = [["", "All"], ["approvals", "Approvals & rejections"], ["page", "Winner pages"], ["site", "Site content"], ["approved_email", "Approved emails"], ["user", "Staff users"]];
const matchKind = (r, kind) => !kind || (kind === "approvals" ? (r.action === "page_approve" || r.action === "page_reject") : r.target_kind === kind);

function short(v) { const s = v == null || v === "" ? "—" : Array.isArray(v) ? v.join(", ") : String(v); return s.length > 80 ? s.slice(0, 77) + "…" : s; }

function Entry({ e }) {
  const [open, setOpen] = useState(false);
  const details = Object.entries(e.details || {});
  return (
    <li className="rounded-2xl bg-white p-4 ring-1 ring-sbg-raspberry/10" data-testid="activity-entry">
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <span className="text-sbg-burgundy">
          <span className="font-bold text-pt-purple">{e.by}</span>
          <span className="ml-1 rounded bg-sbg-blush px-1.5 py-0.5 text-[10px] font-bold uppercase text-sbg-raspberry">{e.by_role}</span>{" "}
          {VERBS[e.action] || e.action.replaceAll("_", " ")}{" "}
          {e.target_kind === "page" ? <Link to={`/bgjcpt/${e.target}`} className={`font-semibold hover:underline ${e.action === "page_approve" ? "text-emerald-700" : e.action === "page_reject" ? "text-red-600" : "text-sbg-fuchsia"}`}>{e.details?.name || e.target}</Link>
            : <span className="font-semibold">{e.target}</span>}
        </span>
        <span className="text-xs text-sbg-burgundy/50">{fmtWhen(e.at)}</span>
      </div>
      {details.length > 0 && (
        <>
          <button onClick={() => setOpen((o) => !o)} className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-sbg-burgundy/60 hover:text-sbg-burgundy">
            {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />} {open ? "Hide" : "Show"} details
          </button>
          {open && (
            <ul className="mt-2 space-y-1 text-xs text-sbg-burgundy/70">
              {details.map(([k, v]) => (
                <li key={k}><span className="font-semibold">{k}:</span>{" "}
                  {v && typeof v === "object" && !Array.isArray(v) && "from" in v ? <>{short(v.from)} → <span className="text-sbg-burgundy">{short(v.to)}</span></> : short(v)}
                </li>
              ))}
              {e.ip && <li className="text-sbg-burgundy/50">from {e.ip}</li>}
            </ul>
          )}
        </>
      )}
    </li>
  );
}

export default function AdminActivity() {
  usePageMeta({ title: "Admin · Activity Log | Southern Black Girls Toolkits", description: "Staff dashboard." });
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [kind, setKind] = useState("");
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!loading && !user?.is_admin) navigate("/admin", { replace: true });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user?.is_admin) return;
    api.get("/admin/activity?limit=300").then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [user]);

  const shown = useMemo(() => (rows || []).filter((r) => matchKind(r, kind)), [rows, kind]);
  const latest = shown[0];
  const rest = showAll ? shown.slice(1) : shown.slice(1, 10);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SiteNav />
      <div className="mx-auto w-full max-w-5xl flex-1 px-5 py-10">
        <button onClick={() => navigate(-1)} className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-sbg-raspberry hover:underline"><ArrowLeft className="h-4 w-4" /> Back</button>
        <h1 className="font-display text-3xl font-black text-sbg-burgundy sm:text-4xl"><Activity className="mr-2 inline h-7 w-7 text-sbg-fuchsia" />Activity Log</h1>
        <p className="mt-2 text-sm text-sbg-burgundy/70">Every change made by admins and managers to winner pages, site content, approved emails and staff users.</p>

        <div className="mt-6 flex flex-wrap gap-2" data-testid="activity-filters">
          {KINDS.map(([k, label]) => (
            <button key={k} onClick={() => setKind(k)} data-testid={`activity-filter-${k || "all"}`}
              className={`rounded-full px-4 py-1.5 text-xs font-bold ${kind === k ? "bg-sbg-fuchsia text-white" : "bg-white text-sbg-burgundy ring-1 ring-sbg-raspberry/15"}`}>{label}</button>
          ))}
        </div>

        {rows === null ? (
          <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-sbg-raspberry" /></div>
        ) : shown.length === 0 ? (
          <p className="mt-8 text-sm text-sbg-burgundy/60" data-testid="activity-empty">No activity logged yet.</p>
        ) : (
          <>
            <h2 className="mt-8 text-xs font-bold uppercase tracking-[0.18em] text-sbg-raspberry">Most recent</h2>
            <ul className="mt-2"><Entry e={latest} /></ul>
            {shown.length > 1 && (
              <>
                <h2 className="mt-8 text-xs font-bold uppercase tracking-[0.18em] text-sbg-raspberry">History ({shown.length - 1})</h2>
                <ul className="mt-2 space-y-2" data-testid="activity-history">{rest.map((e) => <Entry key={e.id} e={e} />)}</ul>
                {shown.length > 10 && (
                  <button onClick={() => setShowAll((s) => !s)} data-testid="activity-show-all" className="mt-4 text-sm font-bold text-sbg-fuchsia hover:underline">
                    {showAll ? "Show less" : `Show all ${shown.length - 1}`}
                  </button>
                )}
              </>
            )}
          </>
        )}
      </div>
      <SiteFooter />
    </div>
  );
}
